/* =============================================================================
 * Quem e' o usuario logado, e o que isso libera.
 *
 * O modelo e' ACUMULATIVO, nao hierarquico: a mesma pessoa pode ser torcedora,
 * atleta e dirigente de clube ao mesmo tempo. Nada de "nivel >= 3".
 *
 * O arquivo tem duas metades, e a diferenca entre elas importa:
 *
 *   IDENTIDADE  (ehAtleta, ehDirigente, ehMaster...) - o que a pessoa E'.
 *               Sai de TAB_USUARIO_PERFIL, que e' N:N e chega em sessao.perfis.
 *   PERMISSAO   (podeGerirClube, podeVerDadosAtleta...) - o que ela pode fazer
 *               COM UM ALVO. Depende do vinculo, nao do tipo de perfil.
 *
 * Ser do tipo 'Clube' nao diz de QUAL clube - isso so' TAB_USUARIO_CLUBE diz.
 * E' por isso que `ehDirigente` e `podeGerirClube(id)` existem separados: da'
 * para ter o perfil e nenhum clube vinculado, e a tela precisa saber a
 * diferenca para explicar em vez de sumir com o atalho.
 *
 * POR QUE A IDENTIDADE E' RECALCULADA AQUI, e nao lida de usuario.UsuarioX:
 * o backend deriva essas flags em LibPrisma.cs, mas so' para os perfis do ERP
 * (Loja, Representante, Cliente, Fornecedor, Vendedor, Marca) - Clube e Atleta
 * ficam de fora. Como `usuario.UsuarioRetaguarda` la' e' calculado ignorando os
 * dois, um dirigente de clube chega aqui marcado como retaguarda. O JS do
 * iPrisma resolve isso do mesmo jeito, refazendo a conta sobre Perfis.
 *
 * ATENCAO: isto e' UX, nao seguranca. Hoje o ExecutarFuncionalidadeController
 * valida o JWT mas descarta o resultado, e e' o cliente que informa
 * Solicitacao.Usuario - qualquer restricao real precisa estar no backend.
 * ========================================================================== */

import { sessao } from './sessao.js';
import { config } from '@shared/lib/config.js';
import { paraInteiro, paraLista, paraTexto } from '@shared/lib/valores.js';

/**
 * Os tipos de TAB_USUARIO_PERFIL que dizem "esta pessoa NAO e' a organizacao da
 * liga". Retaguarda e' quem nao tem nenhum deles - a mesma regra do
 * UsuarioRetaguarda do iPrisma, mas incluindo Clube e Atleta, que a versao do
 * backend esquece.
 *
 * A lista e' maior que o Futigol precisa de proposito: a base de usuarios e' a
 * mesma do Prisma, entao um lojista ou representante pode logar aqui - e nao
 * pode ser confundido com a organizacao da liga.
 *
 * 'VISITANTE' E' O TORCEDOR, e ele estava de fora - o buraco desta lista. E' o
 * perfil que a propria tela de criar conta atribui a quem se cadastra no app
 * (ver pages/usuario/cadastrar-se.js), e sem ele na lista o torcedor caia em
 * `ehRetaguarda` e ganhava `podeGerirClube` de QUALQUER clube: as nominatas dos
 * dois times, a pasta de documentos da partida e a administracao de clube
 * apareciam para quem so' veio ver o placar. Dos dezesseis tipos do
 * EnumTipoPerfil, os unicos que sobram fora daqui sao 'Retaguarda' e
 * 'Administrador' - e esses dois ja' entram por `ehAdmin`.
 */
const PERFIS_DE_DOMINIO = ['Clube', 'Atleta', 'Visitante', 'Cliente', 'Loja', 'Representante', 'Fornecedor', 'Consumidor', 'Vendedor', 'Marca', 'Preposto', 'Agente', 'Consultor', 'Colaborador'];

export function capacidades() {
    const usuario = sessao.usuario;
    const tipos = new Set(paraLista(sessao.perfis).map((p) => paraTexto(p.TipoPerfil)));

    //Master ganha de tudo. A chave de config existe para homologar sem mexer no
    //cadastro - e' a mesma saida do iPrisma.
    const ehMaster = config.usuarioMaster || usuario?.UsuarioMaster === true;
    const ehAdmin = ehMaster || tipos.has('Administrador') || tipos.has('Retaguarda');

    //De QUAL clube a pessoa e' dirigente vem de TAB_USUARIO_CLUBE, nunca dos
    //perfis. PermiteAdministrar separa quem gerencia de quem so' esta' vinculado.
    const clubes = new Set(
        paraLista(sessao.clubesVinculados)
            .filter((c) => c.permiteAdministrar !== false)
            .map((c) => paraInteiro(c.idClube))
            .filter((id) => id > 0)
    );
    const atletas = new Set(paraLista(sessao.atletasVinculados).map((a) => paraInteiro(a.IdAtleta)));
    const banido = usuario?.Banido === true; // V2

    const temPerfil = (tipo) => tipos.has(tipo);
    const temAlgumDominio = PERFIS_DE_DOMINIO.some((t) => tipos.has(t));
    //A organizacao da liga: logada, sem NENHUM papel de dominio e sem nenhum
    //vinculo de clube ou atleta. E' a derivada, nao o perfil 'Retaguarda' - esse
    //ja' entra em ehAdmin. O vinculo conta CRU (inclusive o que nao administra):
    //quem esta' ligado a um clube e' daquele clube, nao da organizacao.
    //
    //A DERIVADA SO' E' SEGURA PORQUE TODO USUARIO TEM PERFIL: o cadastro sempre
    //cria a linha de TAB_USUARIO_PERFIL a partir da regra de usuario (ver
    //CriarTabUsuarioCadastrase.cs). Conta sem perfil nenhum cai aqui como
    //organizacao da liga - se um dia surgir uma dessas, e' esta conta que muda,
    //nao esta regra.
    const temVinculoDeDominio = paraLista(sessao.clubesVinculados).length > 0 || paraLista(sessao.atletasVinculados).length > 0;
    const ehRetaguarda = !!usuario && !temAlgumDominio && !temVinculoDeDominio;

    return {
        /* ---- identidade: o que a pessoa E' ---- */
        autenticado: !!usuario,
        ehMaster,
        ehAdministrador: ehAdmin,
        //Perfil de clube OU vinculo de clube: os dois caminhos existem, e quem
        //tem so' o vinculo (sem o perfil cadastrado) tambem dirige.
        ehDirigente: ehAdmin || clubes.size > 0 || tipos.has('Clube'),
        ehAtleta: atletas.size > 0 || tipos.has('Atleta'),
        //Logado sem NENHUM papel de dominio: a organizacao da liga, o suporte.
        ehRetaguarda,
        ehTorcedor: !!usuario && !banido,
        banido,

        //Os tipos crus, para a tela que precisar de um caso que nao virou
        //predicado - melhor que ela ler sessao.perfis por fora.
        perfis: [...tipos],
        temPerfil,

        /* ---- o que a pessoa TEM ---- */
        clubes: [...clubes],
        atletas: [...atletas],

        //O caso que some da tela sem explicacao: tem o perfil de clube mas
        //nenhum clube vinculado. Quem sabe disso consegue dizer o porque em vez
        //de simplesmente nao mostrar o atalho.
        ehDirigenteSemClube: tipos.has('Clube') && clubes.size === 0,
        ehAtletaSemCadastro: tipos.has('Atleta') && atletas.size === 0,

        /* ---- permissao: o que ela pode fazer COM UM ALVO ---- */
        //Administra o clube quem tem o VINCULO com ele (o dirigente), o master/
        //administrador e a RETAGUARDA - a organizacao da liga mexe no cadastro de
        //qualquer clube, e e' ela que resolve quando o dirigente nao da' conta.
        podeGerirClube: (id) => ehAdmin || ehRetaguarda || clubes.has(paraInteiro(id)),
        podeGerirCompeticao: (competicao) => ehAdmin || ehAdministradorDaCompeticao(usuario, competicao),
        //Mesma regra de podeGerirClube: quem cadastra atleta e' o dirigente do
        //clube, a retaguarda (a liga digitando a ficha que chegou em papel) ou o
        //master. Ver competicoes/inscrever-atleta.js.
        podeInscreverAtleta: (idClube) => ehAdmin || ehRetaguarda || clubes.has(paraInteiro(idClube)),
        podeVerDadosAtleta: (idAtleta) => ehAdmin || atletas.has(paraInteiro(idAtleta)),
        podeFavoritar: () => true, // V1 e' local; V2 exige login
        podeComentar: () => !!usuario && !banido, // V2
        podeVerDiagnostico: () => ehAdmin,
    };
}

/**
 * Quem organiza a competicao.
 *
 * Nao ha' vinculo N:N como o dos clubes (TAB_USUARIO_CLUBE): o unico dono que a
 * competicao declara e' TAB_COMPETICAO.EMAIL_ADMINISTRADOR. E' campo digitado a
 * mao, entao aceita mais de um endereco separado por ';' ou ',' - varias ligas
 * cadastram o presidente e o secretario ali.
 *
 * @param {object} competicao ja' mapeada pela pagina (le `emailAdministrador`)
 */
function ehAdministradorDaCompeticao(usuario, competicao) {
    const meu = paraTexto(usuario?.Email).toLowerCase();
    if (!meu) return false;

    return paraTexto(competicao?.emailAdministrador)
        .toLowerCase()
        .split(/[;,]/)
        .some((email) => email.trim() === meu);
}
